import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { DiscountCodeForm } from '@/pages/DiscountCodeForm'
import { useViewOnly } from '@/components/templates/app-shell'

export interface Redemption {
  id: number
  contact_name: string
  invoice_number: number
  redeemed_on: string
  reduction: string
  source?: 'public' | 'admin'
  actor?: string | null
  reduction_label: string
}

export interface DiscountCode {
  id: number
  code: string
  percent: number | null
  fixed_amount: string | null
  fixed_amount_cents: number | null
  usage_cap: number | null
  starts_on: string | null
  ends_on: string | null
  applies_to: string[]
  availability: string
  membership_levels: MembershipLevelSummary[]
  events: EventSummary[]
  redemption_count: number
  redemptions: Redemption[]
  active: boolean
  archived_at: string | null
}

interface MembershipLevelSummary {
  id: number
  name: string
}

export interface EventSummary {
  id: number
  name: string
}

interface DiscountCodesResponse {
  discount_codes: DiscountCode[]
}

export function DiscountCodesPage({ onOpen }: { onOpen?: (code: DiscountCode) => void }) {
  const viewOnly = useViewOnly()
  const codesQuery = useQuery({
    queryKey: ['discount-codes'],
    queryFn: () => getJSON<DiscountCodesResponse>('/api/v1/discount_codes'),
    retry: false,
  })
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold text-foreground">Discount Codes</h1>
          {viewOnly && <Badge variant="info">View only</Badge>}
        </div>
      </header>
      <Card className="overflow-hidden">
        <CardContent className="divide-y divide-border p-0">
          {!viewOnly && <DiscountCodeForm />}
          <section>
            <h2 className="p-4 pb-3 text-body font-semibold text-foreground">Configured codes</h2>
            {codesQuery.isLoading ? (
              <p className="px-4 pb-4 text-body-sm text-muted-foreground" role="status">
                Loading Discount Codes...
              </p>
            ) : codesQuery.isError ? (
              <div className="px-4 pb-4">
                <Alert>Discount Codes unavailable</Alert>
              </div>
            ) : (
              <DiscountCodesTable codes={codesQuery.data?.discount_codes ?? []} onOpen={onOpen} />
            )}
          </section>
        </CardContent>
      </Card>
    </div>
  )
}

function DiscountCodesTable({
  codes,
  onOpen,
}: {
  codes: DiscountCode[]
  onOpen?: (code: DiscountCode) => void
}) {
  if (codes.length === 0) {
    return <p className="px-4 pb-4 text-body-sm text-muted-foreground">No Discount Codes yet</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Code</TableHead>
          <TableHead className="text-right">Reduction</TableHead>
          <TableHead>Restrictions</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Redemptions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {codes.map((code) => {
          const openCode = () => onOpen?.(code)
          return (
            <TableRow
              key={code.id}
              tabIndex={onOpen ? 0 : undefined}
              className={
                onOpen
                  ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
                  : undefined
              }
              onClick={openCode}
              onKeyDown={(event) => {
                if (!onOpen || (event.key !== 'Enter' && event.key !== ' ')) return
                event.preventDefault()
                openCode()
              }}
            >
              <TableCell className="font-medium">{code.code}</TableCell>
              <TableCell className="text-right tabular-nums">
                {code.percent === null ? code.fixed_amount : `${code.percent}%`}
              </TableCell>
              <TableCell className="max-w-md text-caption text-muted-foreground">
                <span className="block truncate" title={restrictionSummary(code)}>
                  {restrictionSummary(code)}
                </span>
              </TableCell>
              <TableCell>
                <Badge
                  variant={
                    (code.availability ?? 'Available') === 'Available' ? 'success' : 'warning'
                  }
                >
                  {code.availability ?? 'Available'}
                </Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">{code.redemption_count}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

function usageRestriction(code: DiscountCode) {
  return code.usage_cap == null
    ? 'Unlimited uses'
    : `${code.redemption_count} of ${code.usage_cap} uses`
}

function windowRestriction(code: DiscountCode) {
  if (!code.starts_on && !code.ends_on) return 'Any date'
  return `${code.starts_on ?? 'Any date'} – ${code.ends_on ?? 'No end date'}`
}

function levelRestriction(code: DiscountCode) {
  const levels = code.membership_levels ?? []
  if (levels.length === 0) return 'All Membership Levels'
  return levels.map((level) => level.name).join(', ')
}

function originRestriction(code: DiscountCode) {
  const labels: Record<string, string> = {
    application: 'New joins',
    renewal: 'Renewals',
    level_change: 'Level changes',
    event: 'Event registrations',
  }
  const origins = code.applies_to ?? ['application']
  return origins.map((origin) => labels[origin] ?? origin).join(', ')
}

function eventRestriction(code: DiscountCode) {
  const events = code.events ?? []
  if (events.length === 0) return 'All Events'
  return events.map((event) => event.name).join(', ')
}

function restrictionSummary(code: DiscountCode) {
  return [
    usageRestriction(code),
    windowRestriction(code),
    levelRestriction(code),
    eventRestriction(code),
    originRestriction(code),
  ].join(' · ')
}
